import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import ReusableTable from "../../../shared/ReusableTable/ReusableTable";
import basestyle from "../../CSS/Base.module.css";
import dayjs from "dayjs";
import { getAllParticipants, getEntityNamesByPId, getMonthlyListDetails } from "../services/MonthlyCalculations/monthlyServices";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import * as constants from "../../../constants";
import {
    SectionTitle,
    StyledOneChildContainer,
    StyledParentContainer,
    StyledTwoChildContainer,
} from "../StyledComponents/Container";
import { useTranslation } from "react-i18next";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomAutocomplete from "../../../shared/AutoComplete";
import CustomDatePicker from "../../../shared/CustomDate";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import { notifyUser } from "../Utils/notifyUser";
import CommonUtilities from "../Utils/CommonUtilities";
import toast from "react-hot-toast";
import { getClientDetailsByClientName } from "../Utils/ClientSettingsTable";



interface Column {
    id: string;
    label: string;
    minWidth: number | string;
    align?: "center" | "left" | "right";
}

const formattedDate = (dateString: string): string => {
    const date = new Date(dateString + "T00:00:00");
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

const formatAmount = (
    amount: number | null | undefined,
    locale: string = "en-US",
    currency: string = "USD"
): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    return formatter.format(amount ?? 0);
};

interface Participant {
    participantId: number;
    participantName: string;
}

const MonthlyListing = () => {
    // const restrictedRoles = [
    //     constants.ROLES_FINANCIAL_ANALYST,
    // ];
    const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
    const [userRole] = useState<string>(contactInformation.participantAdminProfile);
    const userInfo = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const translate = t as (key: string) => string;
    const dispatch = useDispatch();
    // const filters = useSelector((state: RootState) => state.table.rcbo?.filters || {});
    const retain = useSelector((state: RootState) => state.table?.monthly);

    const [page, setPage] = useState(retain?.page || 1);
    const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
    const [fromDate, setFromDate] = useState<string | null>(retain?.filters.fromDate || null);
    const [toDate, setToDate] = useState<string | null>(retain?.filters.toDate || null);
    const [selectedEntityType, setSelectedEntityType] = useState(retain?.filters.selectedEntityType || "All");
    const [selectedEntityName, setSelectedEntityName] = useState(retain?.filters.selectedEntityName || { id: 0, name: "Select" });
    const [selectedParticipant, setSelectedParticipant] = useState<Participant>(retain?.filters.selectedParticipant || { participantId: 0, participantName: 'All' });

    const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
    const [count, setCount] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const [participantArray, setParticipantArray] = useState<Participant[]>();
    const [entityNameArray, setEntityNameArray] = useState([{ id: 0, name: "Select" }]);
    const [isLoading, setIsLoading] = useState(false);

    const entityTypes = ClientDetails?.allowedPaymentTypes === constants.ALLOWED_PAYMENT_TYPES_NON_DNL
    ? CommonUtilities.entityTypesOptions.filter(type => type !== "Checking")
    : CommonUtilities.entityTypesOptions;
    const handlePageChange = (newPage: number) => {
        setPage(newPage + 1);
    };
const restrictedRoles = [
        ...(ClientDetails?.name !== constants.CLIENT_NAME_DC_GARY ? [constants.ROLES_FINANCIAL_ANALYST] : []), 
    ];
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    const handleSearch = () => {
        if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            if (to < from) {
                toast.error(translate("toDateShouldBeGreaterThanFromDate"));
                return;
            }
        }
        setPage(1);
        getMonthlyListInfo(fromDate, toDate, selectedEntityType, selectedEntityName, selectedParticipant, 1, rowsPerPage);
    };


    const handleClear = () => {
        dispatch(resetTableState({ tableKey: 'monthly' }));
        setFromDate(null);
        setToDate(null);
        setSelectedEntityType("All");
        setSelectedEntityName({ id: 0, name: "Select" });
        setSelectedParticipant({ participantId: 0, participantName: 'All' });
        setPage(1);
        setRowsPerPage(5);
        // setLastSearchFilters(defaultFilters);

        getMonthlyListInfo(null, null, "All", { id: 0, name: "Select" }, { participantId: 0, participantName: 'All' }, 1, 5);
    };


    async function getMonthlyListInfo(
        fromDate: string | null,
        toDate: string | null,
        selectedEntityType: string,
        selectedEntityName: { id: number, name: string },
        selectedParticipant: { participantId: number, participantName: string } | null,
        page: number,
        rowsPerPage: number
    ) {
        setIsLoading(true);
        try {
            const MonthlyVo = {
                clientId: userInfo.clientId,
                participantId: userRole === constants.ADMIN_VIEW ? selectedParticipant?.participantId : userInfo?.defaultParticipantId,
                entityCategory: selectedEntityType,
                ...(selectedEntityType === 'Savings' && {
                    entityId: selectedEntityName.id,
                }),
                entityType: selectedEntityName.name == "0" ? "checking" : selectedEntityName.name,
                startPeriod: fromDate || "",
                endPeriod: toDate || "",
                page: page,
                size: rowsPerPage,
            };
            dispatch(setTableFilters({ tableKey: 'monthly', filters: { fromDate, toDate, selectedEntityType, selectedEntityName, selectedParticipant } }));
            dispatch(setPagination({ tableKey: 'monthly', page: MonthlyVo.page, pageSize: MonthlyVo.size }));

            const responseList = await getMonthlyListDetails(MonthlyVo);
            if (responseList.status === constants.STATUS_CODES.SUCCESS) {
                setCount(responseList.data.totalRecordCount);
                setFilteredData(responseList.data.data ? responseList.data.data.map((item: any) => ({
                    ...item,
                    startPeriod: formattedDate(item.startPeriod),
                    endPeriod: formattedDate(item.endPeriod),
                    amount: formatAmount(item.amount),
                })) : []
                );
            } else {
                setCount(0);
                setFilteredData([]);
                notifyUser(responseList.response.data.message, 'error', translate('monthlyInterestCalculationHistory'));
            }
        } catch (error) {
            console.error("Error fetching MonthlyList data:", error);
        } finally {
            setIsLoading(false);
        }
    }

    async function getAllParticipantsList() {
        setIsLoading(true);
        try {
            const ParticipantVo = { participantName: "", isActive: "Y", clientId: userInfo.clientId };
            const responseList = await getAllParticipants(ParticipantVo);

            if (responseList.status === constants.STATUS_CODES.SUCCESS) {
                const participants = responseList.data.data.map((p: any) => ({
                    participantId: p.participantId,
                    participantName: p.participantName,
                }));

                const updatedParticipants = [{ participantId: 0, participantName: 'All' }, ...participants];

                setParticipantArray(updatedParticipants);
            } else {
                setParticipantArray([]);
            }
        } catch (error) {
            console.error("Error fetching Participants List:", error);
        } finally {
            setIsLoading(false);
        }
    }

    async function getEntityListByPid(pId: number, entityName: string, clientId: number) {
        try {
            const response = await getEntityNamesByPId(pId, entityName, clientId);
            if (response.status == constants.STATUS_CODES.SUCCESS) {
                const entityData = response.data.data;
                setEntityNameArray(entityData);
            }
        } catch (error) {
            console.error("Error handling fund source:", error);
        }
    }

    useEffect(() => {
        getAllParticipantsList();
    }, []);

    useEffect(() => {
        if ((selectedEntityType && selectedEntityType != "All") && selectedEntityType !== "Checking") {
            const pid = userRole === constants.ADMIN_VIEW ? selectedParticipant.participantId : userInfo?.defaultParticipantId
            getEntityListByPid(pid, selectedEntityType, userInfo?.clientId);
        } else if (selectedEntityType === "Checking") {
            setEntityNameArray([{ id: 1, name: 'Checking' }]);
        } else {
            setEntityNameArray([{ id: 0, name: 'Select' }]);
        }
    }, [selectedParticipant, selectedEntityType]);

    useEffect(() => {
        getMonthlyListInfo(
            fromDate,
            toDate,
            selectedEntityType,
            selectedEntityName,
            selectedParticipant,
            page,
            rowsPerPage
        );
    }, [page, rowsPerPage]);


    const AdminColumns: Column[] = [
        { id: "entityCategory", label: translate('entityType'), minWidth: "10%" },
        { id: "entityType", label: translate('entityName'), minWidth: "15%" },
        ...(userRole === constants.ADMIN_VIEW ? [{ id: "participantName", label: translate("participantName"), minWidth: "18%" }] : []),
        { id: "startPeriod", label: translate('statementDate'), minWidth: "10%", align: "center" },
        { id: "endPeriod", label: translate('generatedDate'), minWidth: "10%", align: "center" },
        { id: "amount", label: translate('calculatedInterest'), minWidth: "15%", align: "right" },
        { id: "lastUpdatedBy", label: translate('userName'), minWidth: "10%" },
    ];

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('monthlyInterestCalculations')}</SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomDatePicker
                                    sx={{ width: { xs: "100%", sm: 180 } }}
                                    label={translate('fromDate')}
                                    value={fromDate ? dayjs(fromDate) : null}
                                    onChange={(newValue) => setFromDate(newValue ? newValue.format("YYYY-MM-DD") : null)}
                                />
                                <CustomDatePicker
                                    sx={{ width: { xs: "100%", sm: 180 } }}
                                    label={translate('toDate')}
                                    value={toDate ? dayjs(toDate) : null}
                                    onChange={(newValue) => setToDate(newValue ? newValue.format("YYYY-MM-DD") : null)}
                                />
                                {userRole === constants.ADMIN_VIEW && (
                                    <CustomAutocomplete
                                        size="small"
                                        sx={{ width: { xs: "100%", sm: 250 } }}
                                        options={participantArray || []}
                                        value={selectedParticipant}

                                        onChange={(event, newValue) => {
                                            if (!newValue) return;
                                            const updatedParticipant = newValue;
                                            setSelectedParticipant(updatedParticipant);
                                            setPage(1);

                                            getMonthlyListInfo(
                                                fromDate,
                                                toDate,
                                                selectedEntityType,
                                                selectedEntityName,
                                                updatedParticipant,
                                                1,
                                                rowsPerPage
                                            );
                                        }}
                                        onBlur={() => {
                                            if (!selectedParticipant || !selectedParticipant.participantId) {
                                                const updatedParticipant = { participantId: 0, participantName: 'All' };
                                                setSelectedParticipant(updatedParticipant);
                                                setPage(1)
                                                getMonthlyListInfo(
                                                    fromDate,
                                                    toDate,
                                                    selectedEntityType,
                                                    selectedEntityName,
                                                    updatedParticipant,
                                                    1,
                                                    rowsPerPage
                                                );
                                            }
                                        }}
                                        getOptionLabel={(option) => option.participantName}
                                        isOptionEqualToValue={(option, value) => option.participantId === value?.participantId}
                                        label={translate('participant')}
                                        placeholder={translate('participant')}
                                    />
                                )}
                                <CustomAutocomplete
                                    sx={{ width: { xs: "100%", sm: 250 } }}
                                    options={entityTypes}
                                    value={selectedEntityType}
                                    onChange={(event, newValue) => {
                                        if (!newValue) return;
                                        const valueToSet = newValue;
                                        setSelectedEntityType(valueToSet);
                                        setPage(1)
                                        setEntityNameArray([]);
                                        setSelectedEntityName({ id: 0, name: "Select" });
                                        getMonthlyListInfo(
                                            fromDate,
                                            toDate,
                                            valueToSet,
                                            { id: 0, name: "Select" },
                                            selectedParticipant,
                                            1,
                                            rowsPerPage
                                        );
                                    }}
                                    onBlur={() => {
                                        if (!selectedEntityType) {
                                            const valueToSet = "All";
                                            setSelectedEntityType(valueToSet);
                                            setPage(1)
                                            setEntityNameArray([]);
                                            setSelectedEntityName({ id: 0, name: "Select" });
                                            getMonthlyListInfo(
                                                fromDate,
                                                toDate,
                                                valueToSet,
                                                { id: 0, name: "Select" },
                                                selectedParticipant,
                                                1,
                                                rowsPerPage
                                            );
                                        }
                                    }}
                                    placeholder={translate('entityType')}
                                    label={translate('entityType')}
                                />
                                <CustomAutocomplete
                                    sx={{ width: { xs: "100%", sm: 250 } }}
                                    options={entityNameArray || []}
                                    value={
                                        selectedEntityName
                                    }
                                    onChange={(event, newValue) => {
                                        if (!newValue) return;
                                        const valueToSet = newValue;
                                        setSelectedEntityName(valueToSet);
                                        setPage(1)
                                        getMonthlyListInfo(
                                            fromDate,
                                            toDate,
                                            selectedEntityType,
                                            valueToSet,
                                            selectedParticipant,
                                            1,
                                            rowsPerPage
                                        );
                                    }}
                                    onBlur={() => {
                                        if (!selectedEntityName) {
                                            const valueToSet = { id: 0, name: "Select" };
                                            setSelectedEntityName(valueToSet);
                                            setPage(1)
                                            getMonthlyListInfo(
                                                fromDate,
                                                toDate,
                                                selectedEntityType,
                                                valueToSet,
                                                selectedParticipant,
                                                1,
                                                rowsPerPage
                                            );
                                        }
                                    }}
                                    getOptionLabel={(option) =>
                                        option.id === 0 || option.id === 1 ? option.name : `${option.id}-${option.name}`
                                    }
                                    label={translate('entityName')}
                                    placeholder={translate('entityName')}
                                />
                            </div>
                        </div>

                        <div className="search-fields-container">
                            <div className="left-fields">
                                <ContainedButton onClick={handleSearch}>{translate('search')}</ContainedButton>
                                <OutlinedButton onClick={handleClear}>{translate('clear')}</OutlinedButton>
                            </div>
                           {!restrictedRoles.includes(userInfo?.roles?.roleName ?? "") &&  <div className="right-fields">
                                <ContainedButton onClick={() => navigate("/monthlyCalculations")}>
                                    {translate('selectCriteria')}
                                </ContainedButton>
                            </div>}
                        </div>
                        <ReusableTable
                            columns={AdminColumns}
                            rows={filteredData}
                            count={count}
                            page={page - 1}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            loading={isLoading}
                        />
                        <div className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={() => userRole === constants.ADMIN_VIEW ? navigate("/rcboSummary") : navigate("/summary")}>
                                {translate('back')}
                            </ContainedButton>
                        </div>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default MonthlyListing;
